<template>
  <div class="tenancy-record dashboard">
    <h6
      v-if="isTenant && loading"
      class="font-dark-gray"
    >
      <i class="rotation fa fa-refresh" />
      {{ $t('loading') }}
    </h6>
    <div v-show="!(isTenant && loading) && (workPermits.length || showCreateStandaloneBtn)">
      <h4 class="title-container">
        <div :class="[isTenant ? 'workPermit_title' : '']">
          {{ $t('workPermit.title') }}
        </div>
        <div
          v-if="showCreateStandaloneBtn"
          class="btn-container"
        >
          <div class="hints">
            <div>{{ $t(`termsAndConditions.${regionName}.workPermit.hintForEdinburghTower`,[ $t('workPermit.types.WORK'), 5 ]) }}</div>
            <div>{{ $t(`termsAndConditions.${regionName}.workPermit.hintForOthers`,[ $t('workPermit.types.WORK'), 3 ]) }}</div>
          </div>
          <button
            class="btn--orange"
            @click="() => applySAWorkPermit()"
          >
            {{ $t('standaloneWorkPermits.applySAWorkPermit') }}
          </button>
        </div>
      </h4>
      <div class="dashboard__table">
        <table class="sort_table">
          <thead>
            <tr class="dashboard__table--header">
              <th
                v-for="header in tableHeaders"
                :key="header.name"
                :class="header.className"
                @click="sortBy(header.attrPath)"
              >
                <span>
                  {{ header.name }}
                  <i
                    v-if="sortingAttr === header.attrPath"
                    :class="[reverse ? 'triangle-down' : 'triangle-up']"
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in sortedRecords"
              :key="item.id"
              class="dashboard__table--body"
            >
              <td class="permitNo">
                <a
                  class="link_text"
                  @click="navToPage(item)"
                >
                  {{ item.code }}
                </a>
              </td>
              <td>
                {{ item.contractorName }}
                {{ getCompanyName(item) }}
              </td>
              <td>{{ item.projectType }}</td>
              <td class="search-filter-wptype">
                <span>{{ item.type }}</span>
                <img
                  v-if="item.hasHotWork"
                  src="/ic_hotwork.png"
                  alt="hotwork"
                >
              </td>
              <td>{{ item.building }}</td>
              <td>{{ item.floorsVal }}</td>
              <td>{{ item.location }}</td>
              <td>{{ maskStatusForExternalUser(item.status) | customStatusLabel }}</td>
              <td class="duration-text">
                {{ item.duration }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <SAWorkPermitTnC
      ref="saWpTnCModal"
      okBtnAction="accept"
      :companyName="selectedTrCompanyName"
      @onOkClick="confirmCreateStandaloneWp"
    />
    <HKLModal
      ref="selectTRModal"
      size="medium"
      okBtnAction="confirm"
      @onCloseClick="() => beforeCloseSelectTRModal()"
      @onOkClick="() => selectTRModalConfirm()"
    >
      <div
        slot="title"
        class="modal-pure-text"
      >
        {{ $t('standaloneWorkPermits.selectTenancyRecord') }}
      </div>
      <div>
        <HKLSelect
          :class="errHighlight"
          :value="selectedTradeNameKey"
          :options="tradeNameHklOptions"
          :showSearch="true"
          :placeholder="$t('directory.label.tenantName')"
          @change="onTradeNameChange"
        />
        <HKLSelect
          :class="errHighlight"
          :value="selectedBuilding || undefined"
          :options="buildingHklOptions"
          :showSearch="true"
          :placeholder="$t('directory.label.building')"
          @change="onBuildingChange"
        />
        <div>{{ selectedTradeNameSummary }}</div>
      </div>
    </HKLModal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { getCurrentInstance, computed, ref, unref } from 'vue-demi'
import navigation from '@/utils/navigation'
import notification from '@/utils/notification'
import { aclHelper } from '@/utils/aclHelper'
import { getFloorIndex } from '@/api/buildings/request'
import { permitCodeSortKey } from '@/utils/sortingHelper'
import { filterInvalidWPInDashboard, getDuration, getProjectType, maskStatusForExternalUser } from '@/modules/workPermits/utils'
import { navigate, getBuilding } from '@/api/workPermit/util'
import { WORK_PERMIT_TYPES, WORK_PERMIT_STATUS } from '@/modules/workPermits/consts'
import { getDashboardWorkPermitTypeLabel } from '@/modules/dashboards/workPermitDashboardLabels'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import { standaloneWpRouteNames } from '@/utils/consts'
import i18n from '@/utils/i18n'
import { useStoreGetters } from '@/store/index'
import { useRefModal } from '@/controls/Modal/modal.hook'
import HKLModal from '@/controls/Modal/HKLModal.vue'
import SAWorkPermitTnC from '../workPermits/SAWorkPermitTnC.vue'
import { useSaWpTnC } from '../workPermits/hooks/useTnC'
import { PROJECT_STATUS_FOR_WP } from './consts'

export default {
  name: 'DashboardsWorkPermits',
  components: {
    HKLModal,
    HKLSelect,
    SAWorkPermitTnC,
  },
  props: {
    hasAccess: {
      type: Boolean,
      default: false,
    },
    tenancyRecords: {
      type: Array,
      default: () => [],
    },
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const { isTenant } = useStoreGetters('user', ['isTenant', 'currentUser'])
    const ownTenancies = ref([])
    const selectedTradeNameModel = ref({})
    const selectedBuilding = ref('')
    const tenancyValid = ref(true)
    const selectedTenancy = computed(() => {
      if (unref(ownTenancies).length === 1) return unref(ownTenancies)[0]
      return unref(ownTenancies).find(
        tenancy => tenancy.tradeName === unref(selectedTradeNameModel).value &&
        tenancy.type === unref(selectedTradeNameModel).type &&
        tenancy.building.fullName === unref(selectedBuilding),
      )
    })
    const { hasSaWpTnC } = useSaWpTnC()
    const { modal: saWpTnCModal, open: openSaWpTncModal } = useRefModal()
    const { modal: selectTRModal, open: openSelectTRModal } = useRefModal()
    const beforeCloseSelectTRModal = () => {
      selectedTradeNameModel.value = {}
      selectedBuilding.value = ''
      tenancyValid.value = true
    }
    const selectTRModalConfirm = () => {
      return unref(hasSaWpTnC) ? openSaWpTncModal() : confirmCreateStandaloneWp()
    }
    const applySAWorkPermit = () => {
      if (unref(isTenant) && unref(ownTenancies).length > 1) {
        openSelectTRModal()
      } else {
        unref(hasSaWpTnC) ? openSaWpTncModal() : confirmCreateStandaloneWp()
      }
    }
    const confirmCreateStandaloneWp = () => {
      if (unref(isTenant) && lodash.isEmpty(unref(selectedTenancy))) {
        tenancyValid.value = false
        notification.fireNotification(false,
          i18n.t('standaloneWorkPermits.selectOneTenancyRecord'),
        )
        return
      }
      proxy.createStandaloneWp()
    }
    const selectedTrCompanyName = computed(() => {
      const tenancy = unref(selectedTenancy)
      if (!tenancy) {
        return ''
      }
      if (tenancy.isTenancyUser) {
        return lodash.get(tenancy, 'company.name', '')
      }
      return lodash.get(tenancy, 'tenancyContractors[0].company.name', '') ||
        lodash.get(tenancy, 'contractorUsers[0].TenancyContractor.company.name', '')
    })
    const tradeNameOptions = computed(() => {
      return unref(ownTenancies).map(tenancy => {
        return {
          label: `${tenancy.type} - ${tenancy.tradeName}`,
          value: tenancy.tradeName,
          buildingName: tenancy.building.fullName,
          type: tenancy.type,
        }
      })
    })
    const buildingOptions = computed(() => {
      const buildings = unref(ownTenancies).filter(tenancy =>
        tenancy.tradeName === unref(selectedTradeNameModel).value && tenancy.type === unref(selectedTradeNameModel).type,
      ).map(tenancy => tenancy.building.fullName)

      return buildings.sort()
    })
    const tradeNameHklOptions = computed(() =>
      unref(tradeNameOptions).map((opt) => ({
        name: opt.label,
        value: `${opt.type}|${opt.value}`,
      })),
    )
    const buildingHklOptions = computed(() =>
      unref(buildingOptions).map((name) => ({ name, value: name })),
    )
    const selectedTradeNameKey = computed(() => {
      const model = unref(selectedTradeNameModel)
      if (!model || model.value == null || model.value === '') return undefined
      return `${model.type}|${model.value}`
    })
    const selectedTradeNameSummary = computed(() => {
      const model = unref(selectedTradeNameModel)
      const building = unref(selectedBuilding)
      return model && model.label && building ? `${model.label} - ${building}` : ''
    })
    const onTradeNameChange = (key) => {
      const opt = unref(tradeNameOptions).find((o) => `${o.type}|${o.value}` === key)
      selectedTradeNameModel.value = opt || {}
      if (opt) {
        selectedBuilding.value = opt.buildingName
      }
    }
    const onBuildingChange = (value) => {
      selectedBuilding.value = value || ''
    }
    return {
      emptyValue: i18n.t('emptyValue'),
      tenancyValid,
      errHighlight: computed(() => {
        return { 'error-highlight': unref(tenancyValid) }
      }),
      ownTenancies,
      tradeNameOptions,
      tradeNameHklOptions,
      buildingOptions,
      buildingHklOptions,
      selectedTradeNameKey,
      selectedTradeNameSummary,
      onTradeNameChange,
      onBuildingChange,
      saWpTnCModal,
      openSaWpTncModal,
      selectTRModal,
      selectedBuilding,
      selectedTradeNameModel,
      selectedTenancy,
      selectedTrCompanyName,
      applySAWorkPermit,
      confirmCreateStandaloneWp,
      selectTRModalConfirm,
      beforeCloseSelectTRModal,
      maskStatusForExternalUser,
    }
  },
  data () {
    return {
      loading: true,
      workPermits: [],
      reverse: true,
      sortingAttr: 'duration',
    }
  },

  computed: {
    ...mapGetters('user', ['isTenant', 'isContractor', 'currentUser', 'isStandaloneContractor']),
    ...mapGetters('regions', ['enableWorkPermitsFeature']),
    ...mapGetters('regions', ['regionName']),
    tableHeaders () {
      return [
        {
          attrPath: 'code',
          name: this.$t('workPermit.number'),
          className: 'permitNo',
        },
        {
          attrPath: 'contractorName',
          name: this.$t('workPermit.contractor'),
          className: 'contractor',
        },
        {
          attrPath: 'projectType',
          name: this.$t('projectType'),
          className: 'project_type',
        },
        {
          attrPath: 'type',
          name: this.$t('workPermit.type'),
          className: 'type',
        },
        {
          attrPath: 'building',
          name: this.$t('workPermit.building'),
          className: 'building',
        },
        {
          attrPath: 'floorsVal',
          name: this.$t('workPermit.floor'),
          className: 'floors',
        },
        {
          attrPath: 'location',
          name: this.$t('workPermit.locationWorkArea'),
          className: 'location',
        },
        {
          attrPath: 'status',
          name: this.$t('status.name'),
          className: 'status',
        },
        {
          attrPath: 'duration',
          name: this.$t('workPermit.duration'),
          className: 'duration',
        },
      ]
    },
    showWorkPermits () {
      return (this.isContractor && (
        aclHelper.hasProjectsAccess(true) ||
        aclHelper.hasWorkPermitAccess(true) ||
        aclHelper.hasStandaloneWpAccess(true))
      ) || (this.isTenant && this.hasAccess)
    },
    showCreateStandaloneBtn () {
      let tenantHasSWPAccess = true
      if (this.isTenant) {
        tenantHasSWPAccess = this.tenancyRecords.some((record) => record.accounts.some((account) => {
          return account.id === this.currentUser.id && !lodash.isEmpty(account.TenancyAccount.perms.StandaloneWorkPermits)
        }))
      }
      return this.enableWorkPermitsFeature && (aclHelper.hasStandaloneWpAccess(this.isContractor) || (this.isTenant && (tenantHasSWPAccess || this.isStandaloneContractor)))
    },
    sortedRecords () {
      const workPermitTypes = ['PROJECT', 'WORK']
      let result = this.workPermits.map(item => {
        const workArea = lodash.get(item, 'workArea')
        const location = item.isTenantProject ? lodash.get(item, 'project.projectLocation', []).join(',') : item.location
        let getBuildingCode
        if (item.isTenantProject) {
          getBuildingCode = item.standalone ? lodash.get(item, 'tenancy.building.code') : lodash.get(item, 'project.tenancy.building.code')
        } else {
          getBuildingCode = lodash.get(item, 'building.code')
        }
        const floors = lodash.sortBy(item.floors || [], floor => {
          return getFloorIndex(getBuildingCode, floor)
        })
        return {
          originData: item,
          code: item.code ? item.code : this.emptyValue,
          contractorName: lodash.get(item, 'contractor.name', this.emptyValue),
          type: getDashboardWorkPermitTypeLabel(item.type, this.emptyValue),
          hasHotWork: this.hotWorkRequired(item),
          location: (workPermitTypes.includes(item.type)
            ? location : workArea) || this.emptyValue,
          building: getBuilding(item) || this.emptyValue,
          floorsVal: item.isAllFloors ? this.$t('workPermit.allFloors') : item.floors.length ? floors.join(', ') : this.emptyValue,
          workType: item.workType ? item.workType : this.emptyValue,
          duration: getDuration(item),
          startDate: item.startDate,
          status: item.status,
          projectType: getProjectType(item),
        }
      })
      result = lodash.orderBy(result, item => this.sortByAttr(item))
      return this.reverse ? result.reverse() : result
    },
  },
  async mounted () {
    const excludeProjectStatus = [PROJECT_STATUS_FOR_WP.COMPLETED]
    const excludeModels = {'workers': true}
    if (this.isContractor) {
      excludeProjectStatus.push(PROJECT_STATUS_FOR_WP.CANCELLED)
    }
    if (this.isTenant) {
      excludeProjectStatus.push(PROJECT_STATUS_FOR_WP.WITHDRAWN)
    }
    const excludeStatus = [
      WORK_PERMIT_STATUS.COMPLETED,
      WORK_PERMIT_STATUS.EXPIRED,
      WORK_PERMIT_STATUS.ENDED,
      WORK_PERMIT_STATUS.CANCELLED,
    ]
    const filters = {
      limitAttrs: true,
      '^status': excludeStatus.join(','),
      '^projectStatus': excludeProjectStatus.join(','),
      excludeModels,
      needDlp: this.isContractor,
    }
    if (this.showWorkPermits) {
      const isSilentGet = this.isTenant
      this.workPermits = await this.getAllWorkPermits({ filters, isSilentGet })
      this.loading = false
    }
    this.workPermits = filterInvalidWPInDashboard(this.workPermits)
    if (this.isTenant) {
      this.ownTenancies = lodash.orderBy(await this.getOwnTenancies(this.isTenant), ['type', 'tradeName'], ['asc', 'asc'])
    }
  },
  methods: {
    ...mapActions('workPermit', [
      'getAllWorkPermits',
      'createWorkPermit',
      'getOwnTenancies',
    ]),
    async createStandaloneWp () {
      let request = {
        isTenantProject: this.isTenant,
        type: WORK_PERMIT_TYPES.WORK,
        standalone: true,
      }
      if (this.isTenant) {
        request.contractorId = lodash.get(this.selectedTenancy, 'isTenancyUser', null)
          ? null
          : lodash.get(this.selectedTenancy, 'contractorUsers[0].TenancyContractor.companyId', null)
        request.sourceId = this.selectedTenancy.isTenancyUser
          ? this.selectedTenancy.id
          : lodash.get(this.selectedTenancy, 'contractorUsers[0].TenancyContractor.tenancyId', null)
      } else {
        request.contractorId = this.currentUser.externalId
      }
      const workpermit = await this.createWorkPermit(request)
      if (workpermit) {
        this.gotoCreatePage(workpermit)
      }
    },
    gotoCreatePage (item) {
      const routeName = item.isTenantProject ? standaloneWpRouteNames.tenantStandaloneWp : standaloneWpRouteNames.vendorStandaloneWp
      navigation.toPage(
        routeName,
        { id: item.id },
      )
    },
    navToPage (item) {
      let hoistingId
      const originData = item.originData
      if (originData.type === WORK_PERMIT_TYPES.HOISTING) {
        hoistingId = originData.id
      }
      navigate(originData, hoistingId, { fromViewAll: true })
    },

    sortBy (attributePath) {
      if (this.sortingAttr === attributePath) {
        this.reverse = !this.reverse
      }
      this.sortingAttr = attributePath
    },
    hotWorkRequired (item) {
      return item.type === WORK_PERMIT_TYPES.HOISTING && item.hotWork
    },
    sortByAttr (item) {
      const value = item[this.sortingAttr]
      if (this.sortingAttr === 'code') {
        return permitCodeSortKey(value)
      } else if (this.sortingAttr === 'duration') {
        return new Date(item.startDate).getTime()
      } else if (typeof value === 'string') {
        return value.toUpperCase()
      } else {
        return value
      }
    },
    getCompanyName (item) {
      const data = item.originData
      let result

      if (data.isTenantProject) {
        const path = data.standalone ? 'tenancy.company.name' : 'project.tenancy.company.name'
        result = lodash.get(data, path)
      }
      result = result || this.$t('emptyValue')

      return `(${result})`
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/dashboard/tenant';
@import "~styles/animation";
@import '~styles/popup';

h4 {
  font-family: Georgia;
  font-size: 22px;
  line-height: 25px;
  padding-bottom: 15px;
  margin-bottom: 10px;
  color: #0c2b46;
  border-bottom: 1px solid #e0e0e0;
}
.permitNo {
  width: 12%;
}
.contractor {
  width: 16%;
}
.project_type {
  width: 12%;
}
.type {
  width: 10%;
}
.building {
  width: 15%;
}
.floors {
  width: 10%;
}
.location {
  width: 12%;
}
.status {
  width: 11.5%;
  font-weight: bold;
}
.duration {
  width: 14%;
}
.dashboard__table--body {
  .duration-text {
    padding-right: 30px;
  }
}
.hints {
  display: inline-block;
  color: #E49405;
  margin-right: 15px;

  // to override h4 font-family: Georgia; no idea why spacial font-families are in datatable
  font-size: 14px;
  font-family: Verdana;
  line-height: 20px;
  text-align: right;
}
.btn-container {
  display: flex;
  align-items: center;
}
.title-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}
.hkl-select {
  margin: 10px 0;
  display: block;
}
.modal-pure-text {
  font-size: 18px
}
.workPermit_title {
  font-size: 20px;
  letter-spacing: 0;
  font-weight: bold;
  color: #0C3A58;
  margin-bottom: 5px;
  font-family: Verdana
}

</style>
